"use client";

import React, { FunctionComponent, useState } from "react";
import Image from "next/image";
import { animated, useSpring } from "@react-spring/web";

import {
  INewsletterSignup,
  INewsletterSignupFields,
} from "@/types/generated/contentful";
import RichText from "../richtext";
import { IField } from "@/types/hubspot";
import { validateField } from "@/helpers/formValidation";
import FormFieldError from "../form/fieldError";
import { Spinner } from "../spinner";
import Disclaimer from "../form/disclaimer";
import { AssetDetails } from "contentful";

type Props = {
  content: INewsletterSignup;
};

const NewsletterSignup: FunctionComponent<Props> = ({ content }) => {
  const {
    title,
    breadcrumbImage,
    body,
    successMessage,
    background,
    foreground,
    ctaText,
    ctaColor,
    ctaTextColor,
    showDisclaimer,
    overrideDefaultHubspotForm,
  } = content.fields as INewsletterSignupFields;
  const [error, setError] = useState<string | undefined>(undefined);
  const [email, setEmail] = useState<IField>({
    objectTypeId: "",
    name: "email",
    label: "Email",
    required: true,
    hidden: false,
    fieldType: "email",
    value: "",
    error: "",
  });
  const [isSubmitting, setSubmitting] = useState(false);
  const [isComplete, setCompleted] = useState(false);
  const { display, height, opacity, scale } = useSpring({
    display: isComplete ? "" : "hidden",
    height: isComplete ? "auto" : "0px",
    opacity: isComplete ? 1 : 0,
    scale: isComplete ? 1 : 0,
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    let field = { ...email };
    let submission = {
      fields: { email: email.value },
      context: {
        pageUri: window.location.href,
        pageName: document.title,
      },
    };
    field.error = validateField(email);

    if (field.error) {
      setError(field.error);
      setEmail(field);
    } else {
      setSubmitting(true);
      fetch(
        `/api/form/${overrideDefaultHubspotForm || process.env.NEXT_PUBLIC_NEWSLETTER_FORM}`,
        {
          method: "post",
          headers: new Headers({ "content-type": "application/json" }),
          body: JSON.stringify(submission),
        },
      )
        .then((res) => {
          if (!res.ok) throw res;
          return res.json();
        })
        .then(() => {
          setSubmitting(false);
          setCompleted(true);
        })
        .catch((error) => {
          setSubmitting(false);
          error.json().then((body: any) => {
            field.error = body.error;
            setEmail(field);
          });
        });
    }
  };

  const handleUpdate = (value: string) => {
    let field = { ...email, error: "" };
    field.value = value;
    setEmail(field);
  };

  return (
    <section className="rounded-xl">
      <div
        className={`grid grid-cols-1 items-center gap-4 rounded-xl px-4 py-8 lg:grid-cols-2 ${background ? "md:gap-8 md:px-8 lg:gap-8 lg:px-20 lg:py-16" : ""}`}
        style={{
          backgroundColor: background || "transparent",
          ...(foreground && { color: foreground }),
        }}
      >
        <div className="flex w-full flex-col gap-4">
          <div className="flex flex-col gap-4 xl:gap-6">
            {breadcrumbImage && (
              <Image
                src={breadcrumbImage.fields.file?.url as string}
                alt=""
                width={
                  (breadcrumbImage.fields.file?.details as AssetDetails).image
                    ?.width
                }
                height={
                  (breadcrumbImage.fields.file?.details as AssetDetails).image
                    ?.height
                }
                priority={true}
                unoptimized={true}
              />
            )}
            <h2
              className={`text-3xl font-extrabold !leading-tight tracking-tight xl:text-3xl ${!foreground && "text-gray-900"}`}
            >
              {title}
            </h2>
          </div>
          <div className="opacity-90">
            {body && <RichText content={body} textSize="text-base" />}
          </div>
        </div>
        <div className="p-0 lg:p-2">
          <animated.div
            className="flex flex-col"
            style={{ display: isComplete ? "none" : "" }}
          >
            <form className="flex flex-col" onSubmit={handleSubmit} noValidate>
              <div className="flex w-full flex-col gap-3 md:flex-row md:items-center md:gap-0">
                <input
                  id="newsletter-input"
                  className={`h-11 ${email.error ? "border-red-500 bg-red-50 focus:border-red-500" : "bg-white"} focus:ring-none flex-none appearance-none rounded-lg border border-gray-300 p-2 text-gray-900 focus:ring-0 md:flex-1 md:rounded-r-none`}
                  type="email"
                  placeholder="Enter your email"
                  onChange={(event) => handleUpdate(event.target.value)}
                />
                <button
                  className={`h-11 w-full whitespace-nowrap rounded-lg p-3 text-sm font-medium focus:ring-0 md:w-28 md:rounded-l-none ${!ctaColor && "bg-primary text-white hover:bg-dark-primary"}`}
                  style={
                    ctaColor
                      ? {
                          backgroundColor: ctaColor,
                          color: ctaTextColor || foreground || "white",
                        }
                      : {}
                  }
                >
                  {isSubmitting ? (
                    <div className="inline-block h-4 w-4">
                      <Spinner />
                    </div>
                  ) : ctaText ? (
                    ctaText
                  ) : (
                    "Subscribe"
                  )}
                </button>
              </div>
              {email.error && <FormFieldError field={email} error={error} />}
            </form>
            {showDisclaimer === undefined || (showDisclaimer && <Disclaimer />)}
          </animated.div>
          <animated.div
            className=""
            style={{
              display,
              height,
              opacity,
              scale,
            }}
          >
            <div className="w-full text-center text-xl font-bold">
              {successMessage ? successMessage : "You're subscribed!"}
            </div>
          </animated.div>
        </div>
      </div>
    </section>
  );
};

export default NewsletterSignup;
